import { Patient } from 'src/app/core/api/patient.service';
import { QuestionnaireType } from 'src/API';

/**
 * Clinical priority tiers based on the questionnaire → assessment → follow-up workflow.
 *
 * Workflow: Patient completes OSDI/SPEED → clinic contacts within 2 weeks to book assessment
 *           → re-assess every 6 months (or 3 months if at-risk scores).
 */
export enum ContactPriority {
  URGENT = 'URGENT',         // QR completed >14 days ago, no assessment since → contact overdue
  HIGH = 'HIGH',             // QR completed <14 days ago, no assessment yet → in the 2-week window
  FOLLOW_UP = 'FOLLOW_UP',   // Assessment done after QR, but >180 days ago → reassessment due
  ROUTINE = 'ROUTINE',       // Assessment done <180 days ago → active patient, up to date
  NEW = 'NEW',               // No completed questionnaire on file → not yet in clinical pathway
}

export const CONTACT_PRIORITY_ORDER: { [key: string]: number } = {
  [ContactPriority.URGENT]: 0,
  [ContactPriority.HIGH]: 1,
  [ContactPriority.FOLLOW_UP]: 2,
  [ContactPriority.NEW]: 3,
  [ContactPriority.ROUTINE]: 4,
};

/** Material icon name shown for each priority tier (chip + filter tab + legend). */
export const PRIORITY_ICON_MAP: { [key: string]: string } = {
  [ContactPriority.URGENT]: 'warning',
  [ContactPriority.HIGH]: 'assignment_turned_in',
  [ContactPriority.FOLLOW_UP]: 'schedule',
  [ContactPriority.ROUTINE]: 'check_circle',
  [ContactPriority.NEW]: 'help_outline',
};

export interface LatestQuestionnaire {
  type: string | null;
  config: string | null;
  completedAt: string;
  atRisk: boolean | null;
}

export interface EnrichedPatient extends Partial<Patient> {
  contactPriority: ContactPriority;
  daysSinceAssessment: number | null;
  daysSinceQR: number | null;
  latestQuestionnaire: LatestQuestionnaire | null;
  lastAssessment: any;
  // Precomputed display values so the template never calls functions (change-detection safe).
  // The tooltip is stored as a translation key + params and rendered with the `transloco` pipe,
  // so it stays reactive to language changes (consistent with the rest of the app).
  priorityIcon?: string;
  tooltipKey?: string;
  tooltipParams?: { [param: string]: number | null };
  qrFriendlyName?: string;
}

const ONE_DAY_MS = 86400000;
export const FOLLOW_UP_WINDOW_DAYS = 14;
const REASSESSMENT_INTERVAL_DAYS = 180;

export function daysSince(dateStr: string): number {
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / ONE_DAY_MS);
}

function isAtRisk(scoresJson: string): boolean {
  try {
    const scores = JSON.parse(scoresJson);
    return (
      scores[QuestionnaireType.OSDI] >= 13 ||
      scores[QuestionnaireType.OSDI6] >= 4 ||
      scores[QuestionnaireType.SPEED] >= 5 ||
      scores[QuestionnaireType.SPEEDII] >= 5 ||
      scores[QuestionnaireType.DEQ] > 5
    );
  } catch (error) {
    return false;
  }
}

function computePriority(
  qrCompletedAt: string | null,
  assessmentCreatedAt: string | null,
  atRisk: boolean | null
): ContactPriority {
  if (!qrCompletedAt) {
    return ContactPriority.NEW;
  }

  const qrMs = new Date(qrCompletedAt).getTime();
  const daysSinceQR = Math.floor((Date.now() - qrMs) / ONE_DAY_MS);

  if (!assessmentCreatedAt) {
    // Patient has questionnaire but no assessment at all
    return daysSinceQR >= FOLLOW_UP_WINDOW_DAYS ? ContactPriority.URGENT : ContactPriority.HIGH;
  }

  const assessmentMs = new Date(assessmentCreatedAt).getTime();

  if (assessmentMs < qrMs) {
    // Assessment predates the questionnaire — patient needs a NEW assessment post-QR
    return daysSinceQR >= FOLLOW_UP_WINDOW_DAYS ? ContactPriority.URGENT : ContactPriority.HIGH;
  }

  // Assessment was done AFTER questionnaire — patient is in the follow-up cycle
  const daysSinceAssessment = Math.floor((Date.now() - assessmentMs) / ONE_DAY_MS);
  const followUpInterval = atRisk === true ? 90 : REASSESSMENT_INTERVAL_DAYS;
  return daysSinceAssessment >= followUpInterval ? ContactPriority.FOLLOW_UP : ContactPriority.ROUTINE;
}

export function enrichPatient(
  patient: Partial<Patient>,
  latestCompletedQR: any | null,
  latestAssessment: any | null
): EnrichedPatient {
  let atRisk: boolean | null = null;
  let latestQuestionnaire: LatestQuestionnaire | null = null;

  if (latestCompletedQR) {
    atRisk = latestCompletedQR.response && latestCompletedQR.response.scores
      ? isAtRisk(latestCompletedQR.response.scores)
      : null;
    latestQuestionnaire = {
      type: latestCompletedQR.type || null,
      config: latestCompletedQR.config || null,
      completedAt: latestCompletedQR.completedAt,
      atRisk
    };
  }

  const qrCompletedAt = latestCompletedQR ? latestCompletedQR.completedAt : null;
  const assessmentCreatedAt = latestAssessment ? latestAssessment.createdAt : null;

  const contactPriority = computePriority(qrCompletedAt, assessmentCreatedAt, atRisk);
  const daysSinceAssessment = assessmentCreatedAt ? daysSince(assessmentCreatedAt) : null;
  const daysSinceQR = qrCompletedAt ? daysSince(qrCompletedAt) : null;

  const enriched = patient as EnrichedPatient;
  enriched.lastAssessment = latestAssessment;
  enriched.daysSinceAssessment = daysSinceAssessment;
  enriched.daysSinceQR = daysSinceQR;
  enriched.latestQuestionnaire = latestQuestionnaire;
  enriched.contactPriority = contactPriority;
  return enriched;
}

export function buildQRByPatientMap(questionnaireRequests: any[]): { [patientId: string]: any } {
  const latestQrByPatientId: { [patientId: string]: any } = {};
  (questionnaireRequests || []).forEach(questionnaireRequest => {
    if (!questionnaireRequest.completedAt || !questionnaireRequest.questionnaireRequestPatientId) { return; }
    const patientId = questionnaireRequest.questionnaireRequestPatientId;
    if (!latestQrByPatientId[patientId] || questionnaireRequest.completedAt > latestQrByPatientId[patientId].completedAt) {
      latestQrByPatientId[patientId] = questionnaireRequest;
    }
  });
  return latestQrByPatientId;
}

export function buildAssessmentByPatientMap(assessments: any[]): { [patientId: string]: any } {
  const latestAssessmentByPatientId: { [patientId: string]: any } = {};
  (assessments || []).forEach(assessment => {
    if (!assessment.createdAt) { return; }
    const patientId = assessment.assessmentPatientId || (assessment.patient && assessment.patient.id);
    if (!patientId) { return; }
    if (!latestAssessmentByPatientId[patientId] || assessment.createdAt > latestAssessmentByPatientId[patientId].createdAt) {
      latestAssessmentByPatientId[patientId] = assessment;
    }
  });
  return latestAssessmentByPatientId;
}
